import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';
import moment from 'moment';

import { Combobox, Input } from '@salesforce/design-system-react';

import { DateRange } from '../../../common/components/';
import rangeOptionValues from '../../../common/constants/dateRangeOptionValues';

import './ExperimentAnalyticsFilter.scss';

class ExperimentAnalyticsFilter extends Component {
  constructor(props) {
    super(props);
    this.selectionForJob = this.selectionForJob.bind(this);

    this.startDate = this.startDateByExperiment();
    this.endDate = this.endDateByExperiment();
    this.jobDateRange = null;
    this.jobKey = 0;
  }

  componentDidMount() {
    this.props.actions.modifyExperimentAnalyticsFilter({
      ...this.props.filter,
      job: this.allJobsOption(),
      startDate: this.startDate,
      endDate: this.endDate
    });
  }

  getI18nID(suffix) {
    return `components.experiment.performance.results.filter.${suffix}`;
  }

  /**
   * Filter (actual job_ids)
   */

  allJobsOption() {
    const { formatMessage } = this.props.intl;
    const label = formatMessage({ id: this.getI18nID('allJobs') });
    return { id: 'none', label };
  }

  selectionForJob(job) {
    const label = job.email_name ? job.email_name : job.id;
    return {
      id: job.id,
      label,
      email_name: job.email_name,
      value: job.id,
      job
    };
  }

  startDateByExperiment() {
    return this.props.experiment?.published_at
      ? new Date(this.props.experiment?.published_at)
      : moment()
          .startOf('day')
          .toDate();
  }

  endDateByExperiment() {
    return this.props.experiment?.finished_at
      ? new Date(this.props.experiment?.finished_at)
      : moment()
          .endOf('day')
          .toDate();
  }

  filterOptions() {
    const jobOptions = [this.allJobsOption(), ...this.props.jobs];
    return jobOptions.map(this.selectionForJob);
  }

  selectedFilter() {
    const filterValue = this.props.filter?.job;
    if (filterValue) {
      if (filterValue.id === 'none') {
        return [this.allJobsOption()];
      }
      return [this.selectionForJob(filterValue)];
    }
    return [];
  }

  handleFilterSelection(evt, data) {
    const selection = data.selection && data.selection[0];

    this.props.actions.modifyExperimentAnalyticsFilter({
      ...this.props.filter,
      job: selection
    });
  }

  // Accepts a custom menu item rendering function that becomes a custom component.
  // It should return a React node.
  menuItem(props) {
    const { intl } = this.props;
    const { option } = props;
    let email;
    let sendDate;

    if (option.job.id === 'none') {
      return <div className="job-menu-item">{option.job.label}</div>;
    }
    if (option.job && option.job.email_name) {
      email = (
        <span className="job-row">
          <span className="job-label">
            <FormattedMessage id="components.ecs.analytics.filter.labels.emailName" />
          </span>
          <span className="job-value">{option.job.email_name}</span>
        </span>
      );
    }
    if (option.job && option.job.send_date) {
      const timestamp = formatTimestamp(intl)(option.job.send_date);
      sendDate = (
        <span className="job-row">
          <span className="job-label">
            <FormattedMessage id="components.ecs.analytics.filter.labels.sendDate" />
          </span>
          <span className="job-value">{timestamp}</span>
        </span>
      );
    }

    const jobId = (
      <span className="job-row">
        <span className="job-label">
          <FormattedMessage id="components.ecs.analytics.filter.labels.jobId" />
        </span>
        <span className="job-value">{option.job.id}</span>
      </span>
    );

    return (
      <div className="job-menu-item">
        {email}
        {sendDate}
        {jobId}
      </div>
    );
  }

  /**
   * Date Range
   */
  handleDateRangeChange(selection) {
    if (selection.selectedDate) {
      this.props.actions.modifyExperimentAnalyticsFilter({
        ...this.props.filter,
        ...selection.selectedDate
      });
    }
  }

  renderFilter() {
    const { formatMessage } = this.props.intl;
    const menuRenderer = this.menuItem.bind(this);
    const hasSpinner = this.props.jobsLoading && !this.props.filter.filter;
    const isDraft = this.props?.experiment?.status === 'DRAFT';
    const input = <Input hasSpinner={hasSpinner} />;
    return (
      <Combobox
        labels={{
          label: formatMessage({ id: this.getI18nID('label') })
        }}
        className="combobox-group"
        classNameContainer="filter-combobox wide"
        selection={this.selectedFilter()}
        onRenderMenuItem={menuRenderer}
        hasInputSpinner={hasSpinner}
        input={input}
        options={this.filterOptions()}
        events={{
          onSelect: this.handleFilterSelection.bind(this)
        }}
        variant="readonly"
        singleInputDisabled={isDraft}
      />
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const isDraft = this.props?.experiment?.status === 'DRAFT';
    const defaultRange = isDraft
      ? rangeOptionValues.TODAY
      : rangeOptionValues.CUSTOM;

    return (
      <div className="experiment-analytics-filter">
        <div className="slds-grid">
          <div className="slds-col slds-grow-none">{this.renderFilter()}</div>
          <div className="slds-col slds-grow-none slds-col_bump-left">
            <DateRange
              key={`date-range-for-${this.jobKey}`}
              label={formatMessage({
                id: 'components.ecs.analytics.filter.daterRangeLabel'
              })}
              selectedRange={
                this.jobDateRange
                  ? this.jobDateRange.selectedRangeOption
                  : defaultRange
              }
              startDate={this.startDate || this.startDateByExperiment()}
              endDate={this.endDate || this.endDateByExperiment()}
              onDateRangeChange={this.handleDateRangeChange.bind(this)}
              disabled={isDraft}
            />
          </div>
        </div>
      </div>
    );
  }
}

ExperimentAnalyticsFilter.propTypes = {
  actions: PropTypes.object.isRequired,
  experiment: PropTypes.object,
  filter: PropTypes.object,
  jobs: PropTypes.array,
  jobsLoading: PropTypes.bool,
  location: PropTypes.object
};

export default injectIntl(ExperimentAnalyticsFilter);
